import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useWebhookAlerts } from "@/hooks/admin/useWebhookAlerts";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

export const AdminNotificationBell = () => {
  const { alerts, unreadCount, markAsRead } = useWebhookAlerts();
  const navigate = useNavigate();

  const recentAlerts = alerts?.slice(0, 5) || [];

  const handleAlertClick = (alert: any) => {
    markAsRead.mutate(alert.id);
    navigate("/admin/alerts");
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-destructive text-destructive-foreground';
      case 'warning':
        return 'bg-yellow-500 text-yellow-50';
      case 'info':
        return 'bg-blue-500 text-blue-50';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-semibold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Webhook Alerts</h3>
            {unreadCount > 0 && (
              <Badge variant="destructive">{unreadCount} unread</Badge>
            )}
          </div>

          {recentAlerts.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              No alerts
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {recentAlerts.map((alert: any) => (
                  <div
                    key={alert.id}
                    onClick={() => handleAlertClick(alert)}
                    className={cn(
                      "p-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors",
                      !alert.read_status && "bg-muted"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <Badge className={getSeverityColor(alert.severity)}>
                        {alert.severity}
                      </Badge>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">
                          {alert.stripe_webhook_events?.event_type}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {alert.stripe_webhook_events?.error || "Webhook event failed"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(alert.created_at), "MMM d, h:mm a")}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate("/admin/alerts")}
          >
            View All Alerts
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
